import { useEffect, useRef, useState, type ReactNode } from "react";
import { ApiKeysPanel } from "./ApiKeysPanel";
import { ConnectionPanel } from "./ConnectionPanel";
import { SettingsPanel } from "./SettingsPanel";
import { DOCS_URL, type ConnectionStatus, type Settings } from "../lib/api";

type TabId = "cloudreve" | "rules" | "keys";

const TABS: Array<{ id: TabId; label: string }> = [
  { id: "cloudreve", label: "Connection" },
  { id: "rules", label: "Upload rules" },
  { id: "keys", label: "Keys" },
];

interface Props {
  open: boolean;
  onClose: () => void;
  connection: ConnectionStatus | null;
  settings: Settings | null;
  onConnectionChanged: () => void;
  onSettingsSaved: (settings: Settings) => void;
}

/**
 * Everything that is configuration rather than content, moved off the main
 * screen. The dashboard is for images; this is for the plumbing behind them.
 */
export function SettingsDrawer({
  open,
  onClose,
  connection,
  settings,
  onConnectionChanged,
  onSettingsSaved,
}: Props) {
  const [tab, setTab] = useState<TabId>("cloudreve");
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Escape closes, and focus moves into the panel so the keyboard follows it.
  useEffect(() => {
    if (!open) return;

    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    closeRef.current?.focus();

    // Stop the page behind scrolling while the panel is over it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  // Send the operator straight to the problem when nothing is connected.
  useEffect(() => {
    if (open && connection && !connection.connected) setTab("cloudreve");
  }, [open, connection]);

  if (!open) return null;

  return (
    <div
      className="scrim"
      onMouseDown={(event) => {
        // Only a click on the backdrop itself closes, not a drag out of a field.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="drawer"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
      >
        <header className="drawer__head">
          <span className="label">Settings</span>
          <button
            className="btn btn--quiet"
            type="button"
            onClick={onClose}
            ref={closeRef}
            aria-label="Close settings"
          >
            Close
          </button>
        </header>

        <nav className="drawer__tabs" role="tablist" aria-label="Settings sections">
          {TABS.map((entry) => (
            <button
              key={entry.id}
              className="drawer__tab"
              type="button"
              role="tab"
              aria-selected={tab === entry.id}
              data-active={tab === entry.id}
              onClick={() => setTab(entry.id)}
            >
              {entry.label}
            </button>
          ))}
          <a className="drawer__tab drawer__tab--link" href={DOCS_URL} target="_blank" rel="noreferrer">
            API docs ↗
          </a>
        </nav>

        <div className="drawer__body">
          <TabPanel active={tab === "cloudreve"}>
            <ConnectionPanel status={connection} onChanged={onConnectionChanged} />
          </TabPanel>
          <TabPanel active={tab === "rules"}>
            <SettingsPanel settings={settings} onSaved={onSettingsSaved} />
          </TabPanel>
          <TabPanel active={tab === "keys"}>
            <ApiKeysPanel />
          </TabPanel>
        </div>
      </div>
    </div>
  );
}

/**
 * Inactive tabs stay mounted but hidden, so a half-filled settings form is not
 * thrown away by switching tabs and coming back.
 */
function TabPanel({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <div role="tabpanel" hidden={!active}>
      {children}
    </div>
  );
}
